import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { AuthorizationData } from '../../typings/common';
import {
    FreelancerCustomersTabs,
    FreelancerInfoPanelClickTypes
} from '../../typings/freelancers';
import {
    ClientFreelancerData,
    FreelancerPanelActions
} from '../../typings/state';
import { BetaMark } from '../BetaMark/BetaMark';
import { CurrentUserBlockBody } from '../CurrentUserBlockBody/CurrentUserBlockBody';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './FreelancerInfoPanel.css';
import * as keyset from './FreelancerInfoPanel.i18n';

const i18n = i18nFactory(keyset);

export interface StateProps {
    clientFreelancerData?: ClientFreelancerData;
    isClientFreelancerDataLoading: boolean;
    authorizationData?: AuthorizationData;
    ulogin: string;
}

export interface DispatchProps {
    onInfoPanelClicked: (clickType: FreelancerInfoPanelClickTypes) => void;
}

export interface OwnProps {
    isFreelancerPage: boolean;
}

interface PanelAction {
    name: string;
    link: string;
    onClick: () => void;
}

export type FreelancerInfoPanelProps = StateProps & OwnProps & DispatchProps;

const BLOCK = 'freelancer-info-panel';
const LINK_TO_ALL_SPECIALISTS = UrlHelpers.getFreelancersListUrl();
const LINK_TO_BECOME_A_SPECIALIST = 'https://yandex.ru/adv/freelancers-form';

export class FreelancerInfoPanel extends React.Component<
    FreelancerInfoPanelProps
> {
    render() {
        const { authorizationData, isClientFreelancerDataLoading } = this.props;
        let panel;

        // Если не авторизованны или данные только загружаются, ни чего не отображаем
        if (!authorizationData || isClientFreelancerDataLoading) {
            panel = this.renderEmptyPanel();
        } else {
            panel = this.renderPanel();
        }

        return this.wrapPanel(panel);
    }

    private wrapPanel(panel: React.ReactNode) {
        if (this.props.isFreelancerPage || panel) {
            return <div className={Bem.cls(BLOCK)}>{panel}</div>;
        }
        return panel;
    }

    private renderPanel() {
        const { authorizationData } = this.props;

        const isFreelancer = Boolean(
            authorizationData && authorizationData.isFreelancer
        );

        if (isFreelancer) {
            return this.renderFreelancerPanel();
        }

        return this.renderCustomerPanel();
    }

    private renderFreelancerPanel() {
        const { ulogin, clientFreelancerData } = this.props;

        // Если смотрим страницы клиента
        if (clientFreelancerData && ulogin === clientFreelancerData.login) {
            return this.showMyCustomerPanel(clientFreelancerData);
        } else {
            return this.showIAmFreelancerPanel();
        }
    }

    private renderCustomerPanel() {
        const { clientFreelancerData } = this.props;

        // если есть фрилансер
        if (clientFreelancerData) {
            return this.showMyFreelancerPanel(clientFreelancerData);
        }
        return this.renderEmptyPanel();
    }

    private renderEmptyPanel() {
        const { isFreelancerPage, isClientFreelancerDataLoading } = this.props;
        // нулевое состояние плашки
        if (isFreelancerPage && !isClientFreelancerDataLoading) {
            return this.showBecomeFreelancerPanel();
        }

        return null;
    }

    // плашка "мой клиент"
    private showMyCustomerPanel(user: ClientFreelancerData) {
        const { login, fullName, phone, avatarLink } = user;

        return (
            <>
                <CurrentUserBlockBody
                    title={i18n('customer')}
                    user={{
                        login,
                        name: fullName,
                        phone,
                        avatarLink
                    }}
                    isBeta={true}
                />
                {this.renderActionsMenu(FreelancerPanelActions.myCustomer)}
            </>
        );
    }

    // плашка "мой фрилансер"
    private showMyFreelancerPanel(user: ClientFreelancerData) {
        const { login, fullName, phone, avatarLink } = user;
        const { ulogin, onInfoPanelClicked } = this.props;
        return (
            <>
                <StyledLink
                    cls={Bem.cls(BLOCK, 'link')}
                    href={UrlHelpers.getFreelancerCardUrl(ulogin, login)}
                    onClick={() => {
                        if (onInfoPanelClicked) {
                            onInfoPanelClicked(
                                FreelancerInfoPanelClickTypes.FREELANCER
                            );
                        }
                    }}
                >
                    <CurrentUserBlockBody
                        title={i18n('mySpecialist')}
                        user={{
                            login,
                            name: fullName,
                            phone,
                            avatarLink
                        }}
                        isBeta={true}
                    />
                </StyledLink>
                {this.renderActionsMenu(FreelancerPanelActions.myFreelancer)}
            </>
        );
    }

    // плашка фрилансера
    private showIAmFreelancerPanel() {
        return (
            <>
                <span className={Bem.cls(BLOCK, 'title')}>
                    {i18n('specialistsOfYandexDirect')}
                </span>
                <BetaMark />
                {this.renderActionsMenu(FreelancerPanelActions.myCustomer)}
            </>
        );
    }

    // плашка "стать фрилансером"
    private showBecomeFreelancerPanel() {
        return (
            <>
                <span className={Bem.cls(BLOCK, 'title')}>
                    {i18n('specialistsOfYandexDirect')}
                </span>
                <BetaMark />
                {this.renderActionsMenu(
                    FreelancerPanelActions.becomeFreelancer
                )}
            </>
        );
    }

    private renderActionsMenu(type: FreelancerPanelActions) {
        const {
            onInfoPanelClicked,
            authorizationData,
            clientFreelancerData,
            ulogin
        } = this.props;

        // если нет authorizationData, то и плашки нет
        const { login = '', isFreelancer = false } = authorizationData || {};

        const operatorActsAsClient =
            clientFreelancerData && ulogin === clientFreelancerData.login;

        const allSpecialists = {
            name: i18n('allSpecialists'),
            link: LINK_TO_ALL_SPECIALISTS,
            onClick: isFreelancer
                ? () =>
                      onInfoPanelClicked(
                          FreelancerInfoPanelClickTypes.ALL_SPECIALISTS_BY_FREELANCER
                      )
                : () =>
                      onInfoPanelClicked(
                          FreelancerInfoPanelClickTypes.ALL_SPECIALISTS_BY_CUSTOMER
                      )
        };

        const actions: {
            [key: string]: PanelAction[];
        } = {
            myFreelancer: [allSpecialists],
            myCustomer: compact([
                {
                    name: i18n('myProfile'),
                    link: UrlHelpers.getFreelancerCardUrl(login, login),
                    onClick: () =>
                        onInfoPanelClicked(
                            FreelancerInfoPanelClickTypes.MY_PROFILE
                        )
                },
                operatorActsAsClient && {
                    name: i18n('myOwnCampaigns'),
                    link: UrlHelpers.getOldCampaignListUrl(login),
                    onClick: () =>
                        onInfoPanelClicked(
                            FreelancerInfoPanelClickTypes.MY_OWN_CAMPAIGNS
                        )
                },
                {
                    name: i18n('myCustomers'),
                    link: UrlHelpers.getFreelancerCustomers(
                        FreelancerCustomersTabs.ACTIVE,
                        login
                    ),
                    onClick: () =>
                        onInfoPanelClicked(
                            FreelancerInfoPanelClickTypes.MY_CUSTOMERS
                        )
                },
                allSpecialists
            ]),
            becomeFreelancer: [
                allSpecialists,
                {
                    name: i18n('becomeSpecialist'),
                    link: LINK_TO_BECOME_A_SPECIALIST,
                    onClick: () =>
                        onInfoPanelClicked(
                            FreelancerInfoPanelClickTypes.BECOME_SPECIALIST
                        )
                }
            ]
        };

        return (
            <div className={Bem.cls(BLOCK, 'actions')}>
                {actions[type].map(action => {
                    return (
                        <StyledLink
                            key={action.name}
                            cls={Bem.cls(BLOCK, 'action')}
                            href={action.link}
                            theme={StyledLinkTheme.common}
                            onClick={() => {
                                if (action.onClick) {
                                    action.onClick();
                                }
                            }}
                        >
                            {action.name}
                        </StyledLink>
                    );
                })}
            </div>
        );
    }
}
